/**
 * Report problems, fixed where you see them (2026-10-09, Litsey Creek
 * #092678280; mockup threads/closeout-device-fixes/mockup.html).
 *
 * Closeout used to show a small "dup" chip and stop there. This banner sits
 * above the document viewer and says, per device, what is wrong and the one
 * button that fixes it:
 *
 *   🔴 Test from another device's form → Remove paper test (asks first)
 *   🟠 Report is another device's     → Remove / Move to S/N …
 *   ⚪ Copy of the same report         → Remove copy
 *   🟡 Test date ≠ date on the form    → Set to 9/24/2026
 *   🔵 Another report covers it        → Link it            (2026-10-28)
 *   🔵 Looks like the main's bypass    → Link as bypass of 8" #N49119
 *
 * "Fix N safe ones" does every copy, move and date in one press (never a
 * test removal). Every fix but a test removal has Undo on its toast.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    AlertTriangle,
    CalendarClock,
    ChevronDown,
    ChevronUp,
    Copy,
    Link2,
    Loader2,
    MoveRight,
    Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { confirmDialog } from "@/components/ui/app-dialogs";
import { InfoButton, type InfoTopic } from "@/components/info-button";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

export type ReportFix =
    | { action: "archive_copy"; attachmentId: string }
    | { action: "move_report"; attachmentId: string; toAssetId: string }
    | { action: "remove_paper_test"; testId: string }
    | { action: "set_test_date"; testId: string; date: string }
    | { action: "share_report"; attachmentId: string; toAssetId: string }
    | { action: "link_bypass"; assetId: string; parentAssetId: string };

export type ReportProblemKind =
    | "copy_of_report"
    | "wrong_device_report"
    | "test_from_other_form"
    | "test_date_differs"
    | "report_also_covers"
    | "bypass_not_linked";

export interface ReportProblem {
    key: string;
    kind: ReportProblemKind;
    assetId: string | null;
    serial: string | null;
    attachmentId: string | null;
    testId: string | null;
    fileName: string | null;
    formSerial: string | null;
    savedDate: string | null;
    formDate: string | null;
    fix: ReportFix;
    /** '3/4" DC #4176258' / '8" DCDA #N49119' (the two "link" kinds). */
    deviceName?: string | null;
    mainName?: string | null;
}

type Undo = Record<string, unknown> & { action: string };

/** "2026-09-24" → "9/24/2026". */
export function shortDate(iso: string | null): string {
    const m = iso?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${Number(m[2])}/${Number(m[3])}/${m[1]}` : (iso ?? "");
}

/** What the row says and what its button says — short, scannable. */
export function describeProblem(p: ReportProblem): { says: string; button: string } {
    switch (p.kind) {
        case "test_from_other_form":
            return {
                says: `Test was recorded from S/N ${p.formSerial ?? "another device"}'s form`,
                button: "Remove paper test",
            };
        case "wrong_device_report":
            return p.fix.action === "move_report"
                ? { says: `Report is for S/N ${p.formSerial}`, button: `Move to ${p.formSerial}` }
                : { says: `Report is S/N ${p.formSerial}'s — it already has it`, button: "Remove" };
        case "copy_of_report":
            return { says: "Same report twice", button: "Remove copy" };
        case "test_date_differs":
            return {
                says: `Test says ${shortDate(p.savedDate)}, form says ${shortDate(p.formDate)}`,
                button: `Set ${shortDate(p.formDate)}`,
            };
        case "report_also_covers":
            return {
                says: `This report also covers ${p.deviceName ?? `S/N ${p.serial ?? "?"}`}`,
                button: "Link it",
            };
        case "bypass_not_linked":
            return {
                says: `On the ${p.mainName ?? "main"}'s report, but not linked as its bypass`,
                button: `Link as bypass of ${p.mainName ?? `S/N ${p.formSerial ?? "?"}`}`,
            };
    }
}

/** ⓘ for the rows that need a word of why. */
export function problemHint(p: ReportProblem): InfoTopic | null {
    if (p.kind === "report_also_covers") {
        return {
            title: "One report, two devices",
            lines: [
                `The city's report for the ${p.mainName ?? "main device"} lists this device too.`,
                "Link it and this device gets the same report. It stops holding the packet.",
                "Nothing on the report changes. The customer still gets it once.",
            ],
        };
    }
    if (p.kind === "bypass_not_linked") {
        return {
            title: "What a bypass is",
            lines: [
                "A bypass is the small line next to a big fire line (a DCDA or RPDA).",
                "Linked as the bypass, it shares the big device's city report and filing.",
                "Only link it if it really is that bypass. You can undo it.",
            ],
        };
    }
    return null;
}

/** Safe = has an Undo. A test removal always asks on its own. Linking a bypass
 *  changes the device record, so it is always its own tap. */
export function isSafeFix(p: ReportProblem): boolean {
    return p.fix.action !== "remove_paper_test" && p.fix.action !== "link_bypass";
}

const TONE: Record<ReportProblemKind, string> = {
    test_from_other_form: "bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300",
    wrong_device_report: "bg-orange-100 text-orange-800 dark:bg-orange-950/50 dark:text-orange-300",
    copy_of_report: "bg-muted text-muted-foreground",
    test_date_differs: "bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-300",
    report_also_covers: "bg-sky-100 text-sky-900 dark:bg-sky-950/50 dark:text-sky-300",
    bypass_not_linked: "bg-sky-100 text-sky-900 dark:bg-sky-950/50 dark:text-sky-300",
};
const ICON: Record<ReportProblemKind, typeof Copy> = {
    test_from_other_form: Trash2,
    wrong_device_report: MoveRight,
    copy_of_report: Copy,
    test_date_differs: CalendarClock,
    report_also_covers: Link2,
    bypass_not_linked: Link2,
};

/** Group by device, worst first. */
export function groupByDevice(problems: ReportProblem[]): Array<{ serial: string; items: ReportProblem[] }> {
    const order: Record<ReportProblemKind, number> = {
        test_from_other_form: 0,
        report_also_covers: 1,
        bypass_not_linked: 2,
        wrong_device_report: 3,
        copy_of_report: 4,
        test_date_differs: 5,
    };
    const map = new Map<string, ReportProblem[]>();
    for (const p of problems) {
        const k = p.serial ?? "—";
        map.set(k, [...(map.get(k) ?? []), p]);
    }
    return [...map.entries()]
        .map(([serial, items]) => ({ serial, items: items.sort((a, b) => order[a.kind] - order[b.kind]) }))
        .sort((a, b) => order[a.items[0].kind] - order[b.items[0].kind] || a.serial.localeCompare(b.serial));
}

export function ReportProblemsBanner({ jobId }: { jobId: string }) {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const q = useQuery({
        queryKey: ["close-out-report-problems", jobId],
        queryFn: () => api.get<{ problems: ReportProblem[] }>(`/api/close-out/jobs/${jobId}/report-problems`),
        staleTime: 30_000,
    });
    const problems = useMemo(() => q.data?.problems ?? [], [q.data]);
    const groups = useMemo(() => groupByDevice(problems), [problems]);
    const safe = problems.filter(isSafeFix);

    const refresh = () => {
        qc.invalidateQueries({ queryKey: ["close-out-report-problems", jobId] });
        qc.invalidateQueries({ queryKey: ["close-out-documents", jobId] });
        qc.invalidateQueries({ queryKey: ["close-out-send-off", jobId] });
        qc.invalidateQueries({ queryKey: ["close-out-queue"] });
        qc.invalidateQueries({ queryKey: ["job", jobId] });
    };

    const undoMany = async (undos: Undo[]) => {
        let failed = 0;
        for (const u of undos) {
            await api.post(`/api/close-out/jobs/${jobId}/report-fixes/undo`, { undo: u }).catch(() => (failed += 1));
        }
        refresh();
        if (failed > 0) toast.error(`${failed} couldn't be undone.`);
        else toast.success("Undone.");
    };

    const run = useMutation({
        mutationFn: async (fixes: ReportFix[]) => {
            const undos: Undo[] = [];
            let ok = 0;
            const errors: string[] = [];
            let clockNeedsCheck = false;
            for (const fix of fixes) {
                try {
                    const r = await api.post<{ undo: Undo | null; message: string; clockNeedsCheck?: boolean }>(
                        `/api/close-out/jobs/${jobId}/report-fixes`,
                        { fix },
                    );
                    ok += 1;
                    if (r.undo) undos.push(r.undo);
                    if (r.clockNeedsCheck) clockNeedsCheck = true;
                } catch (err) {
                    const body = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
                    errors.push(body?.message ?? "Couldn't fix one.");
                }
            }
            return { ok, undos, errors, clockNeedsCheck };
        },
        onSuccess: ({ ok, undos, errors, clockNeedsCheck }) => {
            refresh();
            if (ok > 0) {
                toast.success(`Fixed ${ok}.${clockNeedsCheck ? " Check that device's next test date." : ""}`, {
                    duration: 10_000,
                    action: undos.length > 0 ? { label: "Undo", onClick: () => void undoMany(undos) } : undefined,
                });
            }
            if (errors.length > 0) toast.error(`${errors.length} not fixed: ${errors[0]}`, { duration: 12_000 });
        },
    });

    const fixOne = async (p: ReportProblem) => {
        if (p.fix.action === "remove_paper_test") {
            const yes = await confirmDialog({
                title: `Remove the paper test on S/N ${p.serial ?? "this device"}?`,
                message: `It was recorded from S/N ${p.formSerial ?? "another device"}'s form. The device goes back to untested on this job and its next test date goes back to its last real test. This can't be undone here.`,
                confirmLabel: "Remove test",
                destructive: true,
                focusCancel: true,
            });
            if (!yes) return;
        }
        run.mutate([p.fix]);
    };

    if (q.isLoading || problems.length === 0) return null;

    return (
        <div
            className="rounded-xl border-2 border-orange-300 bg-card px-3.5 py-2.5 dark:border-orange-800"
            data-testid="report-problems"
        >
            <div className="flex flex-wrap items-center gap-3">
                <AlertTriangle className="h-5 w-5 flex-none text-orange-600" />
                <span className="text-2xl font-extrabold tabular-nums leading-none">{problems.length}</span>
                <span className="text-sm font-semibold">
                    report problem{problems.length === 1 ? "" : "s"} on {groups.length} device
                    {groups.length === 1 ? "" : "s"}
                </span>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                    {safe.length > 0 && (
                        <Button
                            size="sm"
                            className="bg-orange-700 text-white hover:bg-orange-800"
                            disabled={run.isPending}
                            onClick={() => run.mutate(safe.map((p) => p.fix))}
                        >
                            {run.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                            Fix {safe.length} safe one{safe.length === 1 ? "" : "s"}
                        </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)}>
                        {open ? (
                            <ChevronUp className="mr-1 h-3.5 w-3.5" />
                        ) : (
                            <ChevronDown className="mr-1 h-3.5 w-3.5" />
                        )}
                        {open ? "Hide" : "Show"} devices
                    </Button>
                </div>
            </div>
            {open && (
                <ul className="mt-2 max-h-80 divide-y divide-border overflow-y-auto rounded-lg border border-border">
                    {groups.map((g) => (
                        <li key={g.serial} className="flex flex-wrap items-start gap-2 px-2.5 py-2">
                            <span className="w-20 flex-none pt-1 text-sm font-extrabold tabular-nums">{g.serial}</span>
                            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                                {g.items.map((p) => {
                                    const d = describeProblem(p);
                                    const hint = problemHint(p);
                                    const Icon = ICON[p.kind];
                                    return (
                                        <div key={p.key} className="flex flex-wrap items-center gap-2">
                                            <span
                                                className={cn(
                                                    "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-semibold",
                                                    TONE[p.kind],
                                                )}
                                            >
                                                <Icon className="h-3.5 w-3.5" />
                                                {d.says}
                                            </span>
                                            {hint && <InfoButton topic={hint} />}
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className={cn(
                                                    "h-7 px-2 text-xs",
                                                    p.kind === "test_from_other_form" &&
                                                        "border-red-300 text-red-700 dark:border-red-900 dark:text-red-400",
                                                )}
                                                disabled={run.isPending}
                                                onClick={() => void fixOne(p)}
                                            >
                                                {d.button}
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
